import {
    findCategoryMapping,
    getCategorySourceKey,
    getSizeGroupKey,
    normalizeSizeGroupSizes,
    sourceValuesMatch
} from "./sourceMappingIdentity";

const getCategoryDisplayKey = category =>
    JSON.stringify([
        getCategorySourceKey(category.labelBrandCategoryName, category.posGender),
        getSizeGroupKey(category.labelBrandSizeGroupSizes)
    ]);

// #region SOURCE SIZE FAMILIES
export const buildCategorySizeSourceRows = ({
    sourceItems = [],
    genderMappings = [],
    labelBrandProducts = [],
    categoryFieldName
}) => {
    const buckets = new Map();
    sourceItems.forEach(item => {
        const genderMapping = genderMappings.find(mapping =>
            sourceValuesMatch(mapping?.originalGenderName, item?.gender)
        );
        const posGender = genderMapping?.userGenderName || item?.gender || "-";
        const key = getCategorySourceKey(item?.name, posGender);
        if (!buckets.has(key)) buckets.set(key, { sourceRows: [], products: [] });
        const sourceKey = getCategorySourceKey(item?.name, item?.gender);
        buckets.get(key).sourceRows.push({ ...item, posGender, sourceKey });
    });

    const bucketsBySource = new Map();
    buckets.forEach(bucket => bucket.sourceRows.forEach(row => bucketsBySource.set(row.sourceKey, bucket)));
    labelBrandProducts.forEach(product => {
        const name = categoryFieldName === "Product type" ? product?.productType : product?.category;
        const sourceKey = getCategorySourceKey(name, product?.gender);
        const bucket = bucketsBySource.get(sourceKey);
        if (bucket) bucket.products.push({ sourceKey, sizes: normalizeSizeGroupSizes(product?.sizes) });
    });

    const result = [];
    buckets.forEach(bucket => {
        const families = [];
        const products = [...bucket.products];
        bucket.sourceRows.forEach(row => {
            if (!products.some(product => product.sourceKey === row.sourceKey))
                products.push({ sourceKey: row.sourceKey, sizes: [] });
        });
        products.forEach(product => {
            const family = { sizes: new Set(product.sizes), sources: new Set([product.sourceKey]) };
            // Re-scan after every merge: a bridging product can connect several existing families.
            for (let index = 0; index < families.length; ) {
                const other = families[index];
                const overlaps =
                    (!family.sizes.size && !other.sizes.size) || [...family.sizes].some(size => other.sizes.has(size));
                if (!overlaps) {
                    index++;
                    continue;
                }
                other.sizes.forEach(size => family.sizes.add(size));
                other.sources.forEach(source => family.sources.add(source));
                families.splice(index, 1);
                index = 0;
            }
            families.push(family);
        });
        families.forEach(family => {
            // Keep garment/letter sizes in their source order; display numeric families in numeric order.
            const sizes = normalizeSizeGroupSizes(
                bucket.products.flatMap(product => product.sizes).filter(size => family.sizes.has(size))
            );
            if (sizes.every(size => /^\d+(?:[.,]\d+)?$/.test(size)))
                sizes.sort((a, b) => Number(a.replace(",", ".")) - Number(b.replace(",", ".")));
            bucket.sourceRows.forEach(({ sourceKey, ...row }) => {
                if (family.sources.has(sourceKey)) result.push({ ...row, labelBrandSizeGroupSizes: sizes });
            });
        });
    });
    return result;
};

export const expandCategoryMappingsForSizeGroups = (mappings, sourceRows) => {
    if (!sourceRows.length) return mappings;
    return sourceRows.flatMap(row => {
        const mapping = findCategoryMapping(mappings, row.name, row.gender, row.labelBrandSizeGroupSizes);
        return mapping ? [{ ...mapping, labelBrandSizeGroupSizes: row.labelBrandSizeGroupSizes }] : [];
    });
};
// #endregion

// #region DISPLAY ROWS
// Group only the display rows; original brand genders remain on every draft and saved mapping.
export const groupCategoryMappings = mappings => {
    const groups = new Map();
    mappings.forEach((category, index) => {
        const key = getCategoryDisplayKey(category);
        if (!groups.has(key)) groups.set(key, { key, sourceIndices: [] });
        groups.get(key).sourceIndices.push(index);
    });

    return [...groups.values()].map(group => {
        const rows = group.sourceIndices.map(index => mappings[index]);
        const first = rows[0];
        const hasCategoryConflict = rows.some(
            row =>
                (row.exportName ?? "") !== (first.exportName ?? "") ||
                (row.subCategoryExportName ?? "") !== (first.subCategoryExportName ?? "")
        );
        const hasSizeSystemConflict = rows.some(
            row =>
                String(row.relatedSizeGroupIdFromPOS ?? "") !== String(first.relatedSizeGroupIdFromPOS ?? "") ||
                (row.relatedSizeGroupNameFromPOS ?? "") !== (first.relatedSizeGroupNameFromPOS ?? "")
        );

        return {
            ...group,
            hasCategoryConflict,
            hasSizeSystemConflict,
            category: {
                ...first,
                isChecked: rows.every(row => row.isChecked),
                ...(hasCategoryConflict
                    ? { exportName: "", subCategoryExportName: "", completeCategoryNameFromPOS: "" }
                    : {}),
                ...(hasSizeSystemConflict ? { relatedSizeGroupIdFromPOS: null, relatedSizeGroupNameFromPOS: "" } : {})
            }
        };
    });
};

export const updateCategoryMappingGroup = (mappings, groupKey, updateMapping) =>
    mappings.map(category => (getCategoryDisplayKey(category) === groupKey ? updateMapping(category) : category));
// #endregion
